import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { formatDate, formatApiError } from "../lib/format";
import { Card, Loading } from "../components/UI";
import { Star, MessageSquare, X } from "lucide-react";
import { useAuth } from "../lib/auth";

export default function Feedback() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const reload = async () => {
    if (user.role === "tenant") { setItems([]); setLoading(false); return; }
    const r = await api.get("/feedback");
    setItems(r.data);
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await api.post("/feedback", {
        rating: Number(f.get("rating")),
        message: f.get("message"),
        category: f.get("category"),
        anonymous: f.get("anonymous") === "on",
      });
      toast.success("Thank you for your feedback!");
      setCreating(false);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) return <Loading/>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 fade-up">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#1A2E24]">Feedback</h1>
          <p className="text-[#5E7166] text-sm">{user.role === "tenant" ? "Share your experience with us." : `${items.length} feedback messages.`}</p>
        </div>
        {user.role === "tenant" && <button onClick={() => setCreating(true)} className="btn-gold" data-testid="leave-feedback-btn"><MessageSquare size={16}/> Leave Feedback</button>}
      </div>

      {user.role === "tenant" ? (
        <Card><p className="text-sm text-[#5E7166]">Your feedback helps us improve. Click "Leave Feedback" to share your experience anonymously or with your name.</p></Card>
      ) : items.length === 0 ? (
        <Card><div className="text-sm text-[#5E7166] text-center py-8">No feedback received yet.</div></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map(f => (
            <Card key={f.id} testid={`feedback-${f.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-yellow-500">{"★".repeat(f.rating || 0)}<span className="text-gray-300">{"★".repeat(5 - (f.rating || 0))}</span></div>
                <span className="text-xs text-[#5E7166]">{formatDate(f.created_at)}</span>
              </div>
              <p className="text-sm">{f.message}</p>
              <div className="text-xs text-[#5E7166] mt-2">— {f.anonymous ? "Anonymous" : (f.tenant?.full_name || "Tenant")} • {f.category || "general"}</div>
            </Card>
          ))}
        </div>
      )}

      {creating && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="card-modav p-6 w-full max-w-md" data-testid="feedback-modal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-serif text-xl font-bold">Share Feedback</h3>
              <button onClick={() => setCreating(false)}><X size={20}/></button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Rating</span>
                <select name="rating" required className="input-modav mt-1" data-testid="feedback-rating">
                  <option value="5">★★★★★ Excellent</option><option value="4">★★★★☆ Good</option>
                  <option value="3">★★★☆☆ Average</option><option value="2">★★☆☆☆ Poor</option><option value="1">★☆☆☆☆ Terrible</option>
                </select></label>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Category</span>
                <select name="category" className="input-modav mt-1">
                  <option value="general">General</option><option value="maintenance">Maintenance</option>
                  <option value="management">Management</option><option value="facilities">Facilities</option><option value="other">Other</option>
                </select></label>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Message</span>
                <textarea name="message" required rows={4} className="input-modav mt-1" data-testid="feedback-message"/></label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="anonymous"/> Submit anonymously</label>
              <button type="submit" className="btn-gold w-full justify-center" data-testid="feedback-submit-btn">Send Feedback</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
